/**
 * PayrollRunListPage — Bérszámfejtési futtatások lista
 * DataTable (shared @kgc/ui foundation) + év-filter + státusz badge + "Új futtatás" gomb
 */

import { useTranslation } from '@kgc/i18n';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  DataTable,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@kgc/ui';
import type { ColumnDef } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { PayrollRunDto, PayrollRunStatus } from '../api/payroll-api';
import { usePayrollRuns } from '../hooks/usePayrollRuns';

const MONTHS_HU = [
  'Jan',
  'Feb',
  'Már',
  'Ápr',
  'Máj',
  'Jún',
  'Júl',
  'Aug',
  'Szep',
  'Okt',
  'Nov',
  'Dec',
];

type BadgeVariant = 'default' | 'secondary' | 'destructive' | 'outline';

const STATUS_BADGE: Record<PayrollRunStatus, { variant: BadgeVariant; labelKey: string }> = {
  DRAFT: { variant: 'secondary', labelKey: 'payroll.status.DRAFT' },
  CALCULATED: { variant: 'outline', labelKey: 'payroll.status.CALCULATED' },
  BOLT_APPROVED: { variant: 'outline', labelKey: 'payroll.status.BOLT_APPROVED' },
  CENTRAL_REVIEWED: { variant: 'default', labelKey: 'payroll.status.CENTRAL_REVIEWED' },
  FINALIZED: { variant: 'default', labelKey: 'payroll.status.FINALIZED' },
};

function buildYearOptions(): number[] {
  const currentYear = new Date().getFullYear();
  return [currentYear - 2, currentYear - 1, currentYear];
}

export function PayrollRunListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState<number>(currentYear);

  const { data: runs = [], isLoading, error } = usePayrollRuns({ year });
  const yearOptions = buildYearOptions();

  const columns = useMemo<ColumnDef<PayrollRunDto>[]>(
    () => [
      {
        id: 'period',
        header: t('payroll.runs.period'),
        cell: ({ row }) => {
          const run = row.original;
          const monthLabel = MONTHS_HU[run.month - 1] ?? String(run.month);
          return (
            <span className="font-medium">
              {run.year} {monthLabel}
              {run.isKivaRun && <span className="ml-2 text-xs text-muted-foreground">(KIVA)</span>}
            </span>
          );
        },
      },
      {
        id: 'status',
        header: t('payroll.runs.status'),
        cell: ({ row }) => {
          const badge = STATUS_BADGE[row.original.status];
          return <Badge variant={badge.variant}>{t(badge.labelKey)}</Badge>;
        },
      },
      {
        id: 'employeeCount',
        header: () => <div className="text-right">{t('payroll.runs.employeeCount')}</div>,
        cell: ({ row }) => <div className="text-right">{row.original.employeeCount}</div>,
      },
      {
        id: 'totalGross',
        header: () => <div className="text-right">{t('payroll.runs.totalGross')}</div>,
        cell: ({ row }) => (
          <div className="text-right">{row.original.totalGross.toLocaleString('hu-HU')} Ft</div>
        ),
      },
      {
        id: 'totalNet',
        header: () => <div className="text-right">{t('payroll.runs.totalNet')}</div>,
        cell: ({ row }) => (
          <div className="text-right">{row.original.totalNet.toLocaleString('hu-HU')} Ft</div>
        ),
      },
      {
        id: 'totalEmployerCost',
        header: () => <div className="text-right">{t('payroll.runs.totalEmployerCost')}</div>,
        cell: ({ row }) => (
          <div className="text-right">
            {row.original.totalEmployerCost.toLocaleString('hu-HU')} Ft
          </div>
        ),
      },
    ],
    [t]
  );

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t('payroll.runs.title')}</h1>
        <Button onClick={() => navigate('/payroll/runs/new')}>+ {t('payroll.runs.newRun')}</Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-4">
            <Select value={String(year)} onValueChange={v => setYear(Number(v))}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {yearOptions.map(y => (
                  <SelectItem key={y} value={String(y)}>
                    {y}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={runs}
            isLoading={isLoading}
            error={error ? t('common.error') : null}
            emptyMessage={t('common.noData')}
            getRowId={run => run.id}
            onRowClick={run => navigate(`/payroll/runs/${run.id}`)}
          />
        </CardContent>
      </Card>
    </div>
  );
}
